/**
 * MessageAttachments — the files a chat message carries, below its text.
 * Images show as thumbnails (one large; two to ten in a two-column grid) and
 * everything else as tiles: a kind icon, the name and `PDF · 2.4 MB`. Videos
 * are tiles too, with no poster frame, and so are HEIC/HEIF images, which
 * most browsers can't draw. A thumbnail downloads only once it's on screen —
 * the thread isn't virtualised, so its whole history is mounted. Clicking one
 * opens the viewer at its place in the message. Without a file source there
 * are no bytes to show, so every file is a name-only tile. The note-card
 * layout's `card` variant spans the card and draws tiles larger, with the
 * kind's own tint on the icon square.
 */
import type { EntryAttachment, ThreadFileKind } from '@discovr/types';
import { useCallback, useState, type ReactNode } from 'react';

import { useInView } from '../../../lib/browser/useInView';
import { middleTruncate } from '../../../lib/format';
import { cn } from '../../../lib/utils';
import { FileKindIcon } from './FileKindIcon';
import { fileMetaLabel, hasInlinePreview, isKnownKind } from './fileKinds';
import { fetchableFileId, type FileSource } from './fileSource';
import { openFileLabel } from './moments';

const ROOT_TESTID = 'message-attachments';
const IMAGES_TESTID = 'attachment-images';
const THUMB_TESTID = 'attachment-thumb';
const TILE_TESTID = 'attachment-tile';
const IMAGE_KIND: ThreadFileKind = 'image';
const NAME_MAX_CHARS = 28;
const KIND_ICON_PX = 20;

type ImageLayout = 'single' | 'grid';
const SINGLE_LAYOUT: ImageLayout = 'single';
const GRID_LAYOUT: ImageLayout = 'grid';
const SINGLE_IMAGE_COUNT = 1;

export type AttachmentsVariant = 'bubble' | 'card';
export const ATTACHMENTS_BUBBLE: AttachmentsVariant = 'bubble';
export const ATTACHMENTS_CARD: AttachmentsVariant = 'card';

const ROOT_CLASS: Record<AttachmentsVariant, string> = {
  bubble: 'flex w-[240px] max-w-full flex-col gap-1.5',
  card: 'flex w-full flex-col gap-2',
};
const IMAGES_CLASS: Record<ImageLayout, string> = {
  single: 'block',
  grid: 'grid grid-cols-2 gap-1',
};
const THUMB_CLASS = 'block w-full overflow-hidden rounded-md border border-border bg-muted';
const IMAGE_CLASS: Record<ImageLayout, string> = {
  single: 'block max-h-[240px] w-full object-cover',
  grid: 'block aspect-square w-full object-cover',
};
/** Holds the thumbnail's place while its bytes load. */
const THUMB_PLACEHOLDER_CLASS: Record<ImageLayout, string> = {
  single: 'block aspect-[4/3] w-full',
  grid: 'block aspect-square w-full',
};
const TILE_TEXT_CLASS = 'flex min-w-0 flex-col';
/** A card's image tile shows the picture small, in the icon's square — the composer chip's look, not a full preview. */
const TILE_THUMB_CLASS = 'size-9 shrink-0 rounded-[4px] border border-border object-cover';
const TILE_ICON_BASE_CLASS = 'flex size-9 shrink-0 items-center justify-center rounded-[4px]';
const NEUTRAL_ICON_TONE = 'bg-muted text-muted-foreground';
/** The card tints a kind's icon square where the design does — a PDF reads red. */
const CARD_ICON_TONE: Partial<Record<ThreadFileKind, string>> = {
  pdf: 'bg-red-50 text-red-600',
};

interface TileStyle {
  tile: string;
  name: string;
  meta: string;
}

const TILE_STYLE: Record<AttachmentsVariant, TileStyle> = {
  bubble: {
    tile: 'flex w-full min-w-0 items-center gap-2.5 rounded-md border border-border bg-card p-2 text-left',
    name: 'truncate text-caption-medium text-foreground',
    meta: 'text-[11px]/4 text-muted-foreground',
  },
  card: {
    tile: 'flex w-full min-w-0 items-center gap-3 rounded-[8px] border border-border bg-card p-2.5 text-left',
    name: 'truncate text-[14px]/5 font-medium text-foreground',
    meta: 'text-[12px]/4 text-muted-foreground',
  },
};

function iconToneFor(kind: string, variant: AttachmentsVariant): string {
  if (variant !== ATTACHMENTS_CARD || !isKnownKind(kind)) return NEUTRAL_ICON_TONE;
  return CARD_ICON_TONE[kind] ?? NEUTRAL_ICON_TONE;
}
const OPENABLE_CLASS = 'cursor-pointer transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** An attachment and its place in the message — what the viewer opens at. */
interface PlacedAttachment {
  attachment: EntryAttachment;
  index: number;
}

function placeAttachment(attachment: EntryAttachment, index: number): PlacedAttachment {
  return { attachment, index };
}

function showsThumbnail({ attachment }: PlacedAttachment): boolean {
  return attachment.kind === IMAGE_KIND && hasInlinePreview(attachment.content_type);
}

function showsTile(placed: PlacedAttachment): boolean {
  return !showsThumbnail(placed);
}

function imageLayout(count: number): ImageLayout {
  if (count === SINGLE_IMAGE_COUNT) return SINGLE_LAYOUT;
  return GRID_LAYOUT;
}

/** Opens the viewer at `index`; none when nothing opens (an optimistic bubble). */
function openerAt(onOpen: ((index: number) => void) | undefined, index: number): (() => void) | undefined {
  if (!onOpen) return undefined;
  return function openAttachment(): void {
    onOpen(index);
  };
}

interface OpenableProps {
  onOpen?: () => void;
  label: string;
  className: string;
  testId: string;
  children: ReactNode;
}

/** A button that opens the viewer, or plain content when there's nothing to open. */
function Openable({ onOpen, label, className, testId, children }: OpenableProps) {
  if (!onOpen) {
    return (
      <div className={className} data-testid={testId}>
        {children}
      </div>
    );
  }
  return (
    <button type="button" onClick={onOpen} aria-label={label} title={label} className={cn(className, OPENABLE_CLASS)} data-testid={testId}>
      {children}
    </button>
  );
}

interface AttachmentTileProps {
  attachment: EntryAttachment;
  variant: AttachmentsVariant;
  onOpen?: () => void;
  /** Replaces the kind icon — a card's image tile shows its thumbnail there. */
  leading?: ReactNode;
}

function KindIconSquare({ attachment, variant }: { attachment: EntryAttachment; variant: AttachmentsVariant }) {
  return (
    <span className={cn(TILE_ICON_BASE_CLASS, iconToneFor(attachment.kind, variant))}>
      <FileKindIcon kind={attachment.kind} size={KIND_ICON_PX} aria-hidden />
    </span>
  );
}

function AttachmentTile({ attachment, variant, onOpen, leading }: AttachmentTileProps) {
  const style = TILE_STYLE[variant];
  return (
    <Openable onOpen={onOpen} label={openFileLabel(attachment.name)} className={style.tile} testId={TILE_TESTID}>
      {leading ?? <KindIconSquare attachment={attachment} variant={variant} />}
      <span className={TILE_TEXT_CLASS}>
        <span className={style.name} title={attachment.name}>
          {middleTruncate(attachment.name, NAME_MAX_CHARS)}
        </span>
        <span className={style.meta}>{fileMetaLabel(attachment.kind, attachment.size)}</span>
      </span>
    </Openable>
  );
}

interface AttachmentThumbProps {
  attachment: EntryAttachment;
  layout: ImageLayout;
  variant: AttachmentsVariant;
  fileSource: FileSource;
  onOpen?: () => void;
}

interface AttachmentPreview {
  /** Callback ref for the element whose visibility gates the fetch. */
  observe: (node: HTMLElement | null) => void;
  /** The image to show; undefined until its bytes arrive. */
  src?: string;
  /** It won't load or decode: show the fallback instead. */
  failed: boolean;
  markUndecodable: () => void;
}

/**
 * An image attachment's preview — the one `useFile` call for it, held until
 * the element is on screen. An optimistic image's local preview shows
 * without a fetch.
 */
function useAttachmentPreview(attachment: EntryAttachment, fileSource: FileSource): AttachmentPreview {
  const { ref, inView } = useInView<HTMLElement>();
  const fetched = fileSource.useFile(fetchableFileId(attachment.file_id, attachment.local_url), { enabled: inView });
  const [undecodable, setUndecodable] = useState(false);
  const markUndecodable = useCallback(() => setUndecodable(true), []);
  return { observe: ref, src: attachment.local_url ?? fetched.url, failed: undecodable || fetched.isError, markUndecodable };
}

/** Nothing to show yet, or nothing that will show: the icon holds the place. */
function showsFallback(failed: boolean, src: string | undefined): boolean {
  return failed || !src;
}

/** One image's thumbnail; an image that fails to load or decode falls back to a tile. */
function AttachmentThumb({ attachment, layout, variant, fileSource, onOpen }: AttachmentThumbProps) {
  const { observe, src, failed, markUndecodable } = useAttachmentPreview(attachment, fileSource);
  if (failed) return <AttachmentTile attachment={attachment} variant={variant} onOpen={onOpen} />;
  return (
    <Openable onOpen={onOpen} label={openFileLabel(attachment.name)} className={THUMB_CLASS} testId={THUMB_TESTID}>
      {src ? (
        <img ref={observe} src={src} alt={attachment.name} className={IMAGE_CLASS[layout]} onError={markUndecodable} />
      ) : (
        <span ref={observe} aria-hidden className={THUMB_PLACEHOLDER_CLASS[layout]} />
      )}
    </Openable>
  );
}

interface TileThumbnailProps {
  attachment: EntryAttachment;
  fileSource: FileSource;
}

/** A card tile's small thumbnail; the kind icon holds the square until it shows, or if it never will. */
function TileThumbnail({ attachment, fileSource }: TileThumbnailProps) {
  const { observe, src, failed, markUndecodable } = useAttachmentPreview(attachment, fileSource);
  if (showsFallback(failed, src)) {
    return (
      <span ref={observe} className={cn(TILE_ICON_BASE_CLASS, NEUTRAL_ICON_TONE)}>
        <FileKindIcon kind={attachment.kind} size={KIND_ICON_PX} aria-hidden />
      </span>
    );
  }
  return <img ref={observe} src={src} alt="" className={TILE_THUMB_CLASS} onError={markUndecodable} />;
}

/** In a card every file is a tile, in the message's own order — an image just shows its thumbnail in the square. */
function CardAttachments({ placed, fileSource, onOpen }: Omit<PreviewedAttachmentsProps, 'variant'>) {
  return (
    <>
      {placed.map((item) => (
        <AttachmentTile
          key={item.attachment.file_id}
          attachment={item.attachment}
          variant={ATTACHMENTS_CARD}
          onOpen={openerAt(onOpen, item.index)}
          leading={tileLeadingFor(item, fileSource)}
        />
      ))}
    </>
  );
}

function tileLeadingFor(placed: PlacedAttachment, fileSource: FileSource): ReactNode {
  if (!showsThumbnail(placed)) return undefined;
  return <TileThumbnail attachment={placed.attachment} fileSource={fileSource} />;
}

interface PreviewedAttachmentsProps {
  placed: PlacedAttachment[];
  variant: AttachmentsVariant;
  fileSource: FileSource;
  onOpen?: (index: number) => void;
}

function PreviewedAttachments({ placed, variant, fileSource, onOpen }: PreviewedAttachmentsProps) {
  if (variant === ATTACHMENTS_CARD) return <CardAttachments placed={placed} fileSource={fileSource} onOpen={onOpen} />;
  const images = placed.filter(showsThumbnail);
  const others = placed.filter(showsTile);
  const layout = imageLayout(images.length);
  return (
    <>
      {images.length > 0 ? (
        <div className={IMAGES_CLASS[layout]} data-testid={IMAGES_TESTID} data-layout={layout}>
          {images.map(({ attachment, index }) => (
            <AttachmentThumb
              key={attachment.file_id}
              attachment={attachment}
              layout={layout}
              variant={variant}
              fileSource={fileSource}
              onOpen={openerAt(onOpen, index)}
            />
          ))}
        </div>
      ) : null}
      {others.map(({ attachment, index }) => (
        <AttachmentTile key={attachment.file_id} attachment={attachment} variant={variant} onOpen={openerAt(onOpen, index)} />
      ))}
    </>
  );
}

function NameOnlyTiles({ placed, variant }: { placed: PlacedAttachment[]; variant: AttachmentsVariant }) {
  return (
    <>
      {placed.map(({ attachment }) => (
        <AttachmentTile key={attachment.file_id} attachment={attachment} variant={variant} />
      ))}
    </>
  );
}

export interface MessageAttachmentsProps {
  attachments: EntryAttachment[];
  /** Turns file ids into bytes. Absent → name-only tiles that don't open. */
  fileSource?: FileSource;
  /** Opens the viewer at a file's index in `attachments`. Absent → nothing opens (an optimistic bubble). */
  onOpen?: (index: number) => void;
  className?: string;
  /** `bubble` (default) or the note card's larger, card-wide `card` look. */
  variant?: AttachmentsVariant;
}

export function MessageAttachments({ attachments, fileSource, onOpen, className, variant = ATTACHMENTS_BUBBLE }: MessageAttachmentsProps) {
  const placed = attachments.map(placeAttachment);
  return (
    <div className={cn(ROOT_CLASS[variant], className)} data-testid={ROOT_TESTID}>
      {fileSource ? (
        <PreviewedAttachments placed={placed} variant={variant} fileSource={fileSource} onOpen={onOpen} />
      ) : (
        <NameOnlyTiles placed={placed} variant={variant} />
      )}
    </div>
  );
}
